import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'

import { drugKey, fetchDrug } from '../api/drugs'
import { submitMedicationRequest, type Urgency } from '../api/medications'
import { requestOtp, verifyOtp, type PatientSession } from '../api/patientAuth'
import { Field } from '../components/Field'
import { Icon } from '../components/Icon'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { ApiError } from '../lib/api'
import { setPatientSession } from '../lib/patientSession'

const URGENCIES: { value: Urgency; label: string }[] = [
  { value: 'same_day', label: 'Same day' },
  { value: '24h', label: 'Within 24 hrs' },
  { value: 'routine', label: 'Routine' },
]

const inputCls =
  'w-full rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

/**
 * Medicine request form (M12-T06a, ADR-0028) at `/medicines/request/:slug`.
 * Quantity + area (optional GPS) + urgency + phone OTP → fans the request out to
 * all 10 pharmacies + AMTC. Pharmacies answer to AMTC only; AMTC texts the
 * patient ONE curated offer (T06b). This screen ends at the waiting state — no
 * payment (ADR-0015).
 */
export function MedicineRequest(): ReactNode {
  const { slug = '' } = useParams()
  const navigate = useNavigate()

  const drugQuery = useQuery({
    queryKey: drugKey(slug),
    queryFn: ({ signal }) => fetchDrug(slug, signal),
    retry: (count, err) =>
      err instanceof ApiError && err.code === 'not_found' ? false : count < 2,
  })

  const [quantity, setQuantity] = useState('')
  const [area, setArea] = useState('')
  const [gps, setGps] = useState<{ lat: number; lng: number } | null>(null)
  const [urgency, setUrgency] = useState<Urgency>('same_day')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [session, setSession] = useState<PatientSession | null>(null)
  const [reference, setReference] = useState<string | null>(null)

  const requestMutation = useMutation({
    mutationFn: () => requestOtp(phone.trim()),
    onSuccess: () => setCodeSent(true),
  })
  const verifyMutation = useMutation({
    mutationFn: () => verifyOtp(phone.trim(), code.trim()),
    // Cache the session so "Track your request" opens the offer without a
    // second OTP; a cold visit (reload / SMS link) still re-verifies.
    onSuccess: (s) => {
      setSession(s)
      setPatientSession(s)
    },
  })
  const submitMutation = useMutation({
    mutationFn: () => {
      if (session === null) throw new Error('not verified')
      const a = area.trim()
      return submitMedicationRequest(session.token, {
        drugSlug: slug,
        quantity: quantity.trim(),
        urgency,
        ...(a ? { area: a } : {}),
        ...(gps ? { gpsLat: gps.lat, gpsLng: gps.lng } : {}),
      })
    },
    onSuccess: (res) => setReference(res.reference),
  })

  const useGps = () => {
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition((pos) =>
      setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
    )
  }

  const ready =
    quantity.trim().length > 0 && session !== null && !submitMutation.isPending

  return (
    <PageShell measure={640}>
      <header
        className={`flex items-center gap-1.5 border-b border-line-1 px-3 py-2 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="rounded p-1.5 text-ink-2 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={18} />
        </button>
        <span className="text-[13px] font-medium text-ink-1">
          Request availability
        </span>
      </header>
      {/* One band for the body: everything nested inherits the 640px measure
          through context and no padding value moves, so mobile is unchanged
          by construction. */}
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col"
      >
        {drugQuery.isPending ? (
          <p
            className="px-4 py-12 text-center text-[13px] text-ink-3"
            role="status"
          >
            Loading…
          </p>
        ) : drugQuery.isError ? (
          <div className="px-4 py-12 text-center">
            <p role="alert" className="text-sm font-medium text-bed-bad">
              {drugQuery.error instanceof ApiError &&
              drugQuery.error.code === 'not_found'
                ? 'Medicine not found.'
                : "Couldn't load this medicine."}
            </p>
            <button
              type="button"
              onClick={() => navigate('/medicines')}
              className="mt-3 rounded-full border border-line-2 bg-bg-app px-4 py-1.5 text-[13px] font-medium"
            >
              Back to search
            </button>
          </div>
        ) : reference !== null ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-bed-good-bg text-bed-good">
              <Icon name="check" size={22} />
            </span>
            <h1 className="font-display text-d-xs font-medium tracking-display text-ink-1">
              Request sent
            </h1>
            <p className="text-[13px] text-ink-3">
              {/* M34-T08: no reply time. "Most offers come within 5–15
                  minutes" was a promise nothing measured (the no-wait rule). */}
              We asked all 10 partner pharmacies, and we&apos;ll text you the
              best offer. Your reference is{' '}
              <span className="font-semibold text-ink-1">{reference}</span>.
            </p>
            <button
              type="button"
              onClick={() => navigate(`/medicines/offer/${reference}`)}
              className="mt-2 inline-flex items-center gap-1.5 rounded-r3 bg-accent px-4 py-2 text-sm font-medium text-accent-on focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              Track your request <Icon name="arrow" size={15} />
            </button>
          </div>
        ) : (
          <div className="flex flex-1 flex-col gap-4 px-4 py-4">
            {/* drug summary */}
            <div className="rounded-r3 border border-line-1 bg-bg-soft p-3">
              <div className="flex items-center gap-2">
                <Icon name="pill" size={16} className="text-ink-2" />
                <span className="flex-1 text-[13.5px] font-semibold text-ink-1">
                  {drugQuery.data.generic_name} {drugQuery.data.strength} ·{' '}
                  {drugQuery.data.form}
                </span>
                {drugQuery.data.prescription_only && (
                  <span className="rounded-full bg-accent-bg px-2 py-0.5 text-[10px] font-semibold text-accent-ink">
                    Prescription
                  </span>
                )}
              </div>
              {drugQuery.data.prescription_only && (
                <p className="mt-2 text-[11.5px] leading-snug text-ink-2">
                  <Icon name="info" size={11} /> Prescription required — bring
                  it to the pharmacy. The pharmacist checks it at pickup.
                </p>
              )}
            </div>

            {/* M17-T11 — the shared Field from T09. Labels stay visible and
              keep the same accessible names, so existing assertions hold. */}
            <Field
              label="Quantity"
              type="text"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="e.g. 30 tablets"
            />

            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-ink-1">
                Your area{' '}
                <span className="font-normal text-ink-3">(optional)</span>
              </span>
              <div className="flex gap-2">
                <input
                  type="text"
                  aria-label="Your area"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  placeholder="e.g. East Legon, Comm. 25 Tema"
                  className={inputCls}
                />
                <button
                  type="button"
                  onClick={useGps}
                  aria-pressed={gps !== null}
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-r3 border px-3 text-[12px] font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                    gps
                      ? 'border-bed-good bg-bed-good-bg text-bed-good'
                      : 'border-accent bg-accent-bg text-accent-ink'
                  }`}
                >
                  <Icon name={gps ? 'check' : 'pin'} size={13} />{' '}
                  {gps ? 'Located' : 'GPS'}
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-ink-1">
                How urgent?
              </span>
              <div
                role="radiogroup"
                aria-label="Urgency"
                className="flex flex-wrap gap-1.5"
              >
                {URGENCIES.map((u) => {
                  const on = urgency === u.value
                  return (
                    <button
                      key={u.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setUrgency(u.value)}
                      className={`rounded-full border px-3 py-1.5 text-[12.5px] font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                        on
                          ? 'border-accent bg-accent text-accent-on'
                          : 'border-line-2 bg-bg-app text-ink-1 hover:bg-bg-soft'
                      }`}
                    >
                      {u.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* phone verify */}
            <div className="flex flex-col gap-2">
              <span className="text-[13px] font-medium text-ink-1">
                Phone (we&apos;ll text you the best offer)
              </span>
              {session ? (
                <p className="inline-flex w-fit items-center gap-1 rounded-full bg-bed-good-bg px-2.5 py-1 text-[13px] font-medium text-bed-good">
                  <Icon name="check" size={14} /> Verified · {phone.trim()}
                </p>
              ) : (
                <>
                  <Field
                    label="Phone number"
                    type="tel"
                    placeholder="Phone, e.g. +233…"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    trailing={
                      <button
                        type="button"
                        disabled={
                          phone.trim().length < 8 || requestMutation.isPending
                        }
                        onClick={() => requestMutation.mutate()}
                        className="shrink-0 rounded-r3 border border-accent bg-accent-bg px-3 text-[13px] font-medium text-accent-ink disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                      >
                        {requestMutation.isPending
                          ? 'Sending…'
                          : codeSent
                            ? 'Resend'
                            : 'Send code'}
                      </button>
                    }
                  />
                  {requestMutation.isError && (
                    <p role="alert" className="text-[12px] text-bed-bad">
                      {errMsg(requestMutation.error)}
                    </p>
                  )}
                  {codeSent && (
                    <Field
                      label="Verification code"
                      type="text"
                      inputMode="numeric"
                      placeholder="6-digit code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      trailing={
                        <button
                          type="button"
                          disabled={
                            code.trim().length < 4 || verifyMutation.isPending
                          }
                          onClick={() => verifyMutation.mutate()}
                          className="shrink-0 rounded-r3 bg-accent px-4 text-[13px] font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                        >
                          Verify
                        </button>
                      }
                    />
                  )}
                  {verifyMutation.isError && (
                    <p role="alert" className="text-[12px] text-bed-bad">
                      {errMsg(verifyMutation.error)}
                    </p>
                  )}
                </>
              )}
            </div>

            <button
              type="button"
              disabled={!ready}
              onClick={() => submitMutation.mutate()}
              className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-r3 bg-accent px-4 py-2.5 text-sm font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              {submitMutation.isPending
                ? 'Sending…'
                : 'Ask the partner pharmacies'}
            </button>
            {submitMutation.isError && (
              <p role="alert" className="text-[12px] text-bed-bad">
                {errMsg(submitMutation.error)}
              </p>
            )}
            <p className="text-[11px] leading-snug text-ink-3">
              Fans out to all 10 pharmacies + the AMTC concierge. Pharmacy
              answers go to AMTC, never to you directly — AMTC texts you one
              best offer. Phone verified by OTP · rate-limited · expires after
              24 h.
            </p>
          </div>
        )}
      </PageBand>
    </PageShell>
  )
}

function errMsg(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'rate_limited')
      return 'Too many requests. Please try again later.'
    return err.message
  }
  return 'Something went wrong. Please try again.'
}
